import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MedicalNotesPanel } from '../components/MedicalNotesPanel';
import { CallQueuePanel } from '../components/CallQueuePanel';
import { OnlineToggle } from '../components/OnlineToggle';
import { CallCountCard } from '../components/CallCountCard';
import { NotificationBell } from '../components/NotificationBell';
import { Pill, User as UserIcon, Video } from 'lucide-react';

export const PharmacistDashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  // Tab lives in the URL so the mobile bottom bar can link straight to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'calls';
  const setActiveTab = (v) => setSearchParams({ tab: v }, { replace: true });

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#2c3e50] mb-2" data-testid="pharmacist-dashboard-title">
              {t('لوحة تحكم الصيدلي', 'Pharmacist Dashboard', 'Tableau du pharmacien')}
            </h1>
            <p className="text-gray-600">{t('مرحباً', 'Welcome', 'Bienvenue')} {user?.full_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <OnlineToggle />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="calls" data-testid="pharm-tab-calls">
              <Video size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
              {t('المكالمات', 'Calls', 'Appels')}
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="pharm-tab-notes">
              <Pill size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
              {t('الوصفات والملاحظات', 'Prescriptions & Notes', 'Ordonnances')}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="pharm-tab-profile">
              <UserIcon size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
              {t('الملف الشخصي', 'Profile', 'Profil')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calls" className="mt-4">
            <CallQueuePanel />
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <MedicalNotesPanel />
          </TabsContent>

          <TabsContent value="profile" className="mt-4 space-y-4">
            <CallCountCard />
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-[#2c3e50] mb-4">
                {t('معلومات الملف الشخصي', 'Profile Information', 'Informations du profil')}
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-600 text-sm">{t('الاسم', 'Name', 'Nom')}</p>
                  <p className="text-lg font-semibold">{user?.full_name}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">{t('البريد الإلكتروني', 'Email', 'Email')}</p>
                  <p className="text-lg font-semibold">{user?.email}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">{t('اسم الصيدلية', 'Pharmacy Name', 'Nom de la pharmacie')}</p>
                  <p className="text-lg font-semibold">{user?.pharmacy_name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">{t('رقم الهاتف', 'Phone', 'Téléphone')}</p>
                  <p className="text-lg font-semibold">{user?.phone_number}</p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
